import { useLayoutEffect, useRef, useState } from "react";

import type { EnsurePageLoadedOutcome } from "../utils/ensure-page-loaded-outcome";

import { isWaitingForRestoredPage } from "../utils/is-waiting-for-restored-page";

const DEFAULT_MAX_STALLS = 2;

/**
 * Drives sequential cursor-pagination fetches until a target page is loaded.
 *
 * ## Why this exists
 *
 * List-restore puts the user back on page N of a cursor-paginated list (e.g.
 * All Activity page 5). TanStack infinite queries only hold pages 1…k in memory,
 * so reaching page N requires calling `ensurePageLoaded(N)` — a loop of
 * `fetchNextPage` calls. While that walk runs, callers hide the table and show a
 * spinner via `waitingForPage`.
 *
 * Without a bail-out, these failure modes leave the UI stuck or stranded:
 *
 * 1. **Rejected fetch** — `isFetching` drops to false but `hasNextPage` stays
 *    true, so `isWaitingForRestoredPage` never clears.
 * 2. **Stalled walk** — `ensurePageLoaded` resolves without advancing
 *    `loadedPages` (stale closure / no-op loop). Nothing in the effect deps
 *    changes, so the walk never re-fires.
 * 3. **Cursor exhausted** — the list is shorter than it was, so the walk runs
 *    out of pages before the target. The spinner clears on its own here, but
 *    without a give-up the caller is left pointing at a page past the end.
 *
 * This hook owns the walk, detects all three, and stops waiting.
 *
 * Callers should treat the two bail-out callbacks differently:
 * - **`onGiveUp`** — the walk cannot reach the target page. Prefer showing the
 *   deepest page already in cache (`clampRestoredPageToLoaded`), not page 1.
 * - **`onAnchorMissing`** (on `useRestoreTableAnchorScroll`) — the saved row id
 *   is gone from the dataset. Resetting to page 1 is appropriate there.
 *
 * Dashboard omits `onGiveUp` and simply renders whatever pages loaded.
 *
 * ## Where it is used
 *
 * - `events-table.tsx` — restored table page (`targetPage: currentPage`)
 * - `watchlist-table.tsx` — same
 * - `Dashboard.tsx` — restored infinite-scroll depth (`targetPage: restorePages`,
 *   `enabled` only during a list-restore visit with multiple loaded pages)
 *
 * Paired with `ensurePageLoaded` from `useEventsPaginated` /
 * `useWatchesPaginated`. Returns `waiting-for-query` while the infinite-query
 * cache is still hydrating; the driver re-runs when `ensurePageLoaded` or
 * `loadedPages` change (without bumping `walkAttempt`).
 *
 * @returns `waitingForPage` — true while the target page is not yet in the
 *   query cache and the hook has not given up. Feed this to loading UI and to
 *   page-clamp effects so they do not fight the walk.
 */
export const useRestorePageWalk = (options: {
	enabled?: boolean;
	ensurePageLoaded: (targetPage: number) => Promise<EnsurePageLoadedOutcome>;
	hasNextPage?: boolean;
	/** True when the list query has failed (e.g. page 1 never loaded). */
	isError?: boolean;
	isFetching: boolean;
	loadedPages: number;
	maxStalls?: number;
	onGiveUp?: () => void;
	targetPage: number;
}): { waitingForPage: boolean } => {
	const {
		enabled = true,
		ensurePageLoaded,
		hasNextPage,
		isError = false,
		isFetching,
		loadedPages,
		maxStalls = DEFAULT_MAX_STALLS,
		onGiveUp,
		targetPage,
	} = options;

	const [gaveUp, setGaveUp] = useState(false);
	// walkAttempt is bumped after a stall to re-trigger the driver effect.
	const [walkAttempt, setWalkAttempt] = useState(0);
	// walkSettled bumps when ensurePageLoaded resolves — gates the stall checker.
	const [walkSettled, setWalkSettled] = useState(0);
	// Stall count is keyed to `${targetPage}:${loadedPages}` so real progress resets it.
	const stallCountRef = useRef(0);
	const progressKeyRef = useRef(`${targetPage}:${loadedPages}`);
	const walkInFlightRef = useRef(false);
	/**
	 * Marks which "trip back to the saved page" is current.
	 *
	 * Real-world case: you left All Activity on page 5, hit Back, and we start
	 * loading pages 2 → 3 → 4 → 5. Each page that arrives is progress on the
	 * *same* trip — not a reason to abandon it. If the network fails partway
	 * (say on page 4), we must still stop waiting and show what we have;
	 * otherwise the spinner never clears.
	 *
	 * We only bump this when the destination changes (new target page) or the
	 * walk is turned off/on — i.e. a genuinely new trip — so an older failure
	 * cannot clear the spinner for a walk that already replaced it.
	 */
	const walkGenerationRef = useRef(0);
	/**
	 * The latest `onGiveUp`, read at the moment the walk gives up.
	 *
	 * Real-world case: you left All Activity on page 5, hit Back, and pages 2
	 * and 3 load before page 4 fails. The in-flight walk started when only page
	 * 1 was loaded, so the callback it captured would clamp you to page 1
	 * instead of page 3 — the deepest page we actually have.
	 */
	const onGiveUpRef = useRef(onGiveUp);

	useLayoutEffect(() => {
		onGiveUpRef.current = onGiveUp;
	}, [onGiveUp]);

	const progressKey = `${targetPage}:${loadedPages}`;

	// Reset stall counter whenever loadedPages advances toward targetPage.
	useLayoutEffect(() => {
		if (progressKeyRef.current === progressKey) {
			return;
		}
		progressKeyRef.current = progressKey;
		stallCountRef.current = 0;
	}, [progressKey]);

	// New target or disabled→enabled: start a fresh walk session.
	/* eslint-disable react-hooks/set-state-in-effect -- reset walk state when the target changes */
	useLayoutEffect(() => {
		walkGenerationRef.current += 1;
		setGaveUp(false);
		stallCountRef.current = 0;
		walkInFlightRef.current = false;
		setWalkAttempt(0);
		setWalkSettled(0);
	}, [enabled, targetPage]);
	/* eslint-enable react-hooks/set-state-in-effect */

	const needsWalk = enabled && !gaveUp && targetPage > loadedPages;

	// Driver: call ensurePageLoaded in layout phase so the walk starts before
	// page-1 table chrome paints. Re-runs on walkAttempt after a retried stall.
	// Only one walk loop runs at a time — isFetching is intentionally omitted
	// from deps so toggling fetch state does not start a concurrent walk.
	//
	// `loadedPages` is in the dependency list so React notices page progress,
	// but that must not treat the in-flight load as cancelled. We used to flip
	// a cancelled flag on every cleanup; when page 2 arrived while we were
	// still fetching toward page 5, a later network error was ignored and the
	// loading spinner stayed up forever. walkGenerationRef keeps one trip
	// alive across those page-progress updates.
	useLayoutEffect(() => {
		if (!needsWalk || walkInFlightRef.current) {
			return;
		}

		const generation = walkGenerationRef.current;
		walkInFlightRef.current = true;

		void ensurePageLoaded(targetPage)
			.then((outcome) => {
				if (generation !== walkGenerationRef.current) {
					return;
				}
				// Query cache not hydrated yet — the driver re-runs when
				// ensurePageLoaded or loadedPages change. Do not bump walkAttempt
				// here; that caused an infinite layout-effect loop.
				//
				// There is deliberately no attempt counter for this branch: nothing
				// it could count is driven by us, so it could never reach a limit.
				// A query that never hydrates ends in an error instead, and the
				// `isError` check in isWaitingForRestoredPage clears the spinner.
				if (outcome === "waiting-for-query") {
					return;
				}
				setWalkSettled((value) => value + 1);
			})
			.catch(() => {
				// Same trip as when we started — even if pages landed in between,
				// a failed fetch must give up so the spinner can clear. The ref, not
				// this effect's `onGiveUp`, so the caller clamps to those pages.
				if (generation !== walkGenerationRef.current) {
					return;
				}
				setGaveUp(true);
				onGiveUpRef.current?.();
			})
			.finally(() => {
				if (generation === walkGenerationRef.current) {
					walkInFlightRef.current = false;
				}
			});
	}, [ensurePageLoaded, loadedPages, needsWalk, targetPage, walkAttempt]);

	// Stall checker: runs after each settled walk attempt. If loadedPages still
	// has not reached targetPage (and we are not mid-fetch), count a stall.
	// After maxStalls no-op resolutions, give up; otherwise bump walkAttempt
	// so the driver effect re-fires.
	/* eslint-disable react-hooks/set-state-in-effect -- stall detection after a settled walk attempt */
	useLayoutEffect(() => {
		if (!needsWalk || walkSettled === 0) {
			return;
		}
		if (isFetching) {
			return;
		}

		const stillWaiting = isWaitingForRestoredPage({
			currentPage: targetPage,
			hasNextPage,
			isError,
			isFetching,
			loadedPages,
		});
		if (!stillWaiting) {
			stallCountRef.current = 0;
			// Reaching here means we stopped waiting while still short of the
			// target page (`needsWalk` guarantees that), so the walk cannot get
			// there: the cursor ran out or the query errored.
			//
			// Real-world case: you left All Activity on page 5, alerts got
			// resolved while you were away, and now the list is only 3 pages long.
			// Give up so the caller puts you on page 3 rather than leaving you on
			// an empty page 5.
			setGaveUp(true);
			onGiveUpRef.current?.();
			return;
		}

		stallCountRef.current += 1;
		if (stallCountRef.current >= maxStalls) {
			setGaveUp(true);
			onGiveUpRef.current?.();
			return;
		}

		setWalkAttempt((value) => value + 1);
	}, [
		hasNextPage,
		isError,
		isFetching,
		loadedPages,
		maxStalls,
		needsWalk,
		targetPage,
		walkSettled,
	]);
	/* eslint-enable react-hooks/set-state-in-effect */

	const waitingForPage =
		needsWalk &&
		isWaitingForRestoredPage({
			currentPage: targetPage,
			hasNextPage,
			isError,
			isFetching,
			loadedPages,
		});

	return { waitingForPage };
};
